import {
  ReadonlyURLSearchParams,
  useSearchParams,
  useRouter,
  usePathname,
} from "next/navigation";
import { useEffect, useRef } from "react";
import {
  FILTER_CATEGORIES,
  QUERY_URL_PARAM,
  RESULT_PAGE_URL_PARAM,
} from "../constants";
import { UiState } from "../types";
import { getMappedSearchParams } from "../utils/mapFacetParams";

export default function useSearchInitialState(
  indexName: string,
  queryString: string = "",
) {
  const router = useRouter();
  const pathname = usePathname();
  const nextSearchParams = useSearchParams();
  const hasUpdatedUrl = useRef(false);

  const searchParamsFromQueryString = new URLSearchParams(
    queryString,
  ) as unknown as ReadonlyURLSearchParams;
  const searchParams = queryString.length
    ? searchParamsFromQueryString
    : nextSearchParams;

  // Map params and update URL if needed (without page refresh)
  const { paramsArray, urlParams, hasChanges } = getMappedSearchParams(
    searchParams,
    indexName,
  );

  useEffect(() => {
    // Only update URL if there are changes and we haven't already updated
    if (hasChanges && !hasUpdatedUrl.current && pathname) {
      hasUpdatedUrl.current = true;
      const newUrl = `${pathname}?${urlParams.toString()}`;
      // router.replace updates URL without page refresh
      router.replace(newUrl, { scroll: false });
    }
  }, [hasChanges, urlParams, pathname, router]);

  //Algolia has no export for it's UiState type
  const uiState: UiState = {
    [indexName]: {},
  };

  //query
  const query = searchParams.get(QUERY_URL_PARAM);
  if (query) {
    uiState[indexName] = {
      query: query,
    };
  }

  //facets - with mapping applied based on index
  const params: { [key: string]: string[] } = {};
  paramsArray.forEach(([key, value]: [string, string]) => {
    if (params[key]) {
      params[key].push(value);
    } else {
      params[key] = [value];
    }
  });

  let refinements: { [key: string]: any } = {};
  FILTER_CATEGORIES.forEach((facet) => {
    if (params[facet]) {
      refinements[facet] = params[facet];
    }
  });
  uiState[indexName].refinementList = { ...refinements };

  //page
  const pageParam = Number(searchParams.get(RESULT_PAGE_URL_PARAM) || "");
  if (pageParam) {
    uiState[indexName].page = pageParam;
  }

  //unable to pass the initial query here since we have to support multiple indexes
  return uiState;
}
